import {
  DeleteOutline,
  DeleteOutlined,
  EditOutlined,
} from '@mui/icons-material';
import {
  Checkbox,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium';
import {
  useGroupsBulkUpdateMutation,
  usePostGroupsQuery,
  useLazyPostMenusQuery,
} from 'api/ordering-service/ordering-service';
import {
  useLocationsToQueryString,
  useSearch,
} from 'components/search/search.hooks';
import {
  defaultSearchParams,
  querySearchParam,
  stringListSearchParam,
  stringSearchParam,
} from 'components/search/search.utils';
import { useHasPermission } from 'hooks/authorization.hooks';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LocationsInfo from './groups-data-grid/locations-info/locations-info.component';
import { DateTime } from 'luxon';
import { ToolbarProps } from 'components/inc-data-grid/inc-data-grid.component';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import { additionalPropertiesToObject } from 'api/ordering-service/ordering-service.utils';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { UtilityDrawerHookReturnType } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import { TransformGroupResult } from 'api/ordering-service/ordering-service.types';
import { useBulkEditDataGridContext } from 'components/inc-data-grid/bulk-edit-data-grid/bulk-edit-data-grid.context';
import {
  displayInfoColumnVisibilityModel,
  useMenuManagementBulkEditFields,
  useMenuManagementDisplayInfoColumns,
} from 'pages/menu-management/menu-management.hooks';
import {
  toBulkUpdateField,
  toBulkUpdateValue,
} from 'pages/menu-management/menu-management-data-grid.utils';
import IncActiveInactiveStatusChip from 'components/inc-data-grid/cells/inc-active-inactive-status-chip/inc-active-inactive-status-chip.component';
import { BulkEditGridColDef } from 'components/inc-data-grid/bulk-edit-data-grid.types';

export const useGroupsFilters = (isImported = true) => {
  const { t } = useTranslation();
  const locationsToQueryString = useLocationsToQueryString('');
  const [triggerMenus] = useLazyPostMenusQuery();

  const statusOptions = useMemo(
    () => [
      {
        label: t('GroupsDataGrid.filter.status.allOption'),
        id: 'ALL',
      },
      {
        label: t('GroupsDataGrid.filter.status.active'),
        id: 'ACTIVE',
      },
      {
        label: t('GroupsDataGrid.filter.status.inactive'),
        id: 'INACTIVE',
      },
    ],
    [t],
  );

  const typeOptions = useMemo(
    () => [
      {
        label: t('GroupsDataGrid.filter.type.allOption'),
        id: 'ALL',
      },
      {
        label: t('GroupsDataGrid.filter.type.modifier'),
        id: 'OPTION_GROUP',
      },
      {
        label: t('GroupsDataGrid.filter.type.catalog'),
        id: 'CATALOG_GROUP',
      },
    ],
    [t],
  );

  const searchParams = useMemo(
    () => [
      ...defaultSearchParams({
        count: 10,
        page: 0,
        sortBy: 'title',
        sortDirection: 'ASC',
      }),
      querySearchParam('title', {
        category: t('GroupsDataGrid.filter.title'),
      }),
      querySearchParam('originalTitle', {
        category: t('GroupsDataGrid.filter.externalTitle'),
      }),
      querySearchParam('incentivioIds', {
        category: t('GroupsDataGrid.filter.id'),
      }),
      querySearchParam('externalIds', {
        category: t('GroupsDataGrid.filter.externalId'),
      }),
      stringSearchParam('includeStores', {
        defaultValue: 'true',
      }),
      stringSearchParam('imported', {
        defaultValue: isImported ? 'true' : 'false',
      }),
      stringListSearchParam('catalogIds', {
        defaultValue: ['ALL'],
        options: [
          {
            label: t('GroupsDataGrid.filter.menu.allOption'),
            id: 'ALL',
          },
        ],
        asyncOptions: async () => {
          const catalogs = await triggerMenus({
            count: 300,
            page: 0,
            storeIds: '*',
            imported: isImported ? 'true' : 'false',
          });
          const dataArray = catalogs?.data?.catalogs.map(catalog => ({
            label: catalog.title,
            id: catalog.catalogId,
          }));

          return [
            {
              label: t('GroupsDataGrid.filter.menu.allOption'),
              id: 'ALL',
            },
          ].concat(dataArray ?? []);
        },
        toQueryString: locationsToQueryString,
        filter: {
          type: 'MultiSelectFilter',
          label: t('GroupsDataGrid.filter.menu.label'),
        },
      }),
      stringSearchParam('status', {
        defaultValue: 'ALL',
        options: statusOptions,
        filter: {
          type: 'SingleSelectFilter',
          label: t('GroupsDataGrid.filter.status.label'),
        },
        toQueryString: value => (value === 'ALL' ? '' : value),
      }),
      stringSearchParam('type', {
        defaultValue: 'ALL',
        options: typeOptions,
        filter: {
          type: 'SingleSelectFilter',
          label: t('GroupsDataGrid.filter.type.label'),
        },
        toQueryString: value => (value === 'ALL' ? '' : value),
      }),
    ],
    [
      t,
      isImported,
      locationsToQueryString,
      statusOptions,
      typeOptions,
      triggerMenus,
    ],
  );

  const modifiedSearchParams = useMemo(() => {
    if (!isImported) {
      return searchParams.filter(
        ({ paramName }) =>
          paramName !== 'originalTitle' && paramName !== 'externalIds',
      );
    }
    return searchParams;
  }, [isImported, searchParams]);

  return useSearch(usePostGroupsQuery, modifiedSearchParams);
};

export const useGroupsDataGrid = (
  isImported: boolean,
  deleteConfirmationDrawer: UtilityDrawerHookReturnType<TransformGroupResult>,
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasEditGroupPermission = useHasPermission('EDIT_GROUP');
  const hasDeleteGroupPermission = useHasPermission('DELETE_GROUP');
  const displayInfoColumns = useMenuManagementDisplayInfoColumns();

  const getActions = useCallback(
    params => {
      const actions = [];

      if (hasEditGroupPermission) {
        actions.push(
          <GridActionsCellItem
            icon={<EditOutlined />}
            label={t('GroupsDataGrid.actions.edit')}
            onClick={() => navigate(`../${params.id}`)}
            showInMenu
          />,
        );
      }

      if (hasDeleteGroupPermission) {
        actions.push(
          <GridActionsCellItem
            icon={<DeleteOutline color="error" />}
            label={t('GroupsDataGrid.actions.delete.label')}
            onClick={() => {
              deleteConfirmationDrawer.setDrawerData(params.row);
              deleteConfirmationDrawer.setOpen(true);
            }}
            showInMenu
          />,
        );
      }

      return actions;
    },
    [
      deleteConfirmationDrawer,
      hasDeleteGroupPermission,
      hasEditGroupPermission,
      navigate,
      t,
    ],
  );

  const columns: BulkEditGridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: t('GroupsDataGrid.column.title.label'),
        minWidth: 500,
        hideable: false,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'originalTitle',
        headerName: t('GroupsDataGrid.column.externalTitle.label'),
        minWidth: 240,
        hideable: true,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'storeInfos',
        headerName: t('GroupsDataGrid.column.locations.label'),
        minWidth: 400,
        hideable: true,
        display: 'flex',
        renderCell: params => <LocationsInfo stores={params.value} />,
      },
      {
        field: 'groupType',
        headerName: t('GroupsDataGrid.column.type.label'),
        minWidth: 80,
        hideable: true,
        display: 'flex',
        renderCell: params => (
          <Typography>
            {params.value === 'CATALOG_GROUP' ? 'Catalog' : 'Modifier'}
          </Typography>
        ),
      },
      {
        field: 'groupId',
        headerName: t('GroupsDataGrid.column.groupId.label'),
        minWidth: 220,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'externalId',
        headerName: t('GroupsDataGrid.column.externalId.label'),
        minWidth: 280,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'inheritParentOptions',
        headerName: t('GroupsDataGrid.column.inheritParent.label'),
        minWidth: 180,
        hideable: true,
        display: 'flex',
        renderCell: params => (
          <Checkbox defaultChecked={params.value} disabled />
        ),
      },
      {
        field: 'minItemSelections',
        headerName: t('GroupsDataGrid.column.minItem.label'),
        minWidth: 180,
        hideable: true,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'maxItemSelections',
        headerName: t('GroupsDataGrid.column.maxItem.label'),
        minWidth: 180,
        hideable: true,
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'extendedAttributes',
        headerName: t('GroupsDataGrid.column.extendedAttributes.label'),
        minWidth: 300,
        hideable: true,
        sortable: false,
        type: 'custom',
        customType: 'keyValue',
        display: 'flex',
        renderCell: params => (
          <Typography title={JSON.stringify(params.value)}>
            {Object.keys(params.value).length > 0 &&
              JSON.stringify(params.value)}
          </Typography>
        ),
      },
      {
        field: 'startDate',
        headerName: t('GroupsDataGrid.column.startDate.label'),
        minWidth: 200,
        hideable: true,
        display: 'flex',
        renderCell: params => (
          <Typography>
            {DateTime.fromISO(params.value).toLocaleString(
              DateTime.DATETIME_MED,
            )}
          </Typography>
        ),
      },
      {
        field: 'endDate',
        headerName: t('GroupsDataGrid.column.endDate.label'),
        minWidth: 200,
        hideable: true,
        display: 'flex',
        renderCell: params => (
          <Typography>
            {DateTime.fromISO(params.value).toLocaleString(
              DateTime.DATETIME_MED,
            )}
          </Typography>
        ),
      },
      {
        field: 'displayRank',
        headerName: t('GroupsDataGrid.column.displayRank.label'),
        hideable: true,
        minWidth: 150,
        type: 'number',
        display: 'flex',
        renderCell: params => <Typography>{params.value}</Typography>,
      },
      {
        field: 'status',
        headerName: t('GroupsDataGrid.column.status.label'),
        hideable: false,
        minWidth: 100,
        flex: 1,
        type: 'singleSelect',
        valueOptions: [
          { id: 'Active', label: 'Active' },
          { id: 'Inactive', label: 'Inactive' },
        ],
        display: 'flex',
        renderCell: params => (
          <IncActiveInactiveStatusChip value={params.value} />
        ),
      },
      ...displayInfoColumns,
      ...(!hasEditGroupPermission && !hasDeleteGroupPermission
        ? []
        : [
            {
              field: t('SearchDataGrid.column.actions'),
              type: 'actions',
              hideable: false,
              getActions,
              resizable: false,
            } as GridColDef,
          ]),
    ],
    [
      displayInfoColumns,
      getActions,
      hasDeleteGroupPermission,
      hasEditGroupPermission,
      t,
    ],
  );

  return !isImported
    ? columns.filter(
        column =>
          column.field !== 'originalTitle' && column.field !== 'externalId',
      )
    : columns;
};

export const useGroupsToolbar = (
  bulkDeleteConfirmationDrawer: UtilityDrawerHookReturnType<TransformGroupResult>,
): {
  toolbar: ToolbarProps;
  initialState: GridInitialStatePremium;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [triggerBulkEdit] = useGroupsBulkUpdateMutation();
  const { selectedRowIds } = useBulkEditDataGridContext();

  const hasEditGroupPermission = useHasPermission('EDIT_GROUP');
  const hasDeleteGroupPermission = useHasPermission('DELETE_GROUP');

  const snackbarForBulkEdit = field => {
    switch (field) {
      case 'status':
        return t('GroupsDataGrid.bulkEdit.status.successMessage');
      case 'displayRank':
        return t('GroupsDataGrid.bulkEdit.displayRank.successMessage');
      case 'extendedAttributes':
        return t('GroupsDataGrid.bulkEdit.extendedAttributes.successMessage');
      case 'startDate':
        return t('GroupsDataGrid.bulkEdit.startDate.successMessage');
      case 'endDate':
        return t('GroupsDataGrid.bulkEdit.endDate.successMessage');
      case 'displayTitle':
        return t('GroupsDataGrid.bulkEdit.displayTitle.successMessage');
      case 'shortDescription':
        return t('GroupsDataGrid.bulkEdit.shortDescription.successMessage');
      case 'longDescription':
        return t('GroupsDataGrid.bulkEdit.longDescription.successMessage');
      case 'smallImage':
        return t('GroupsDataGrid.bulkEdit.smallImage.successMessage');
      case 'mediumImage':
        return t('GroupsDataGrid.bulkEdit.mediumImage.successMessage');
    }
  };

  const modifieldValue = (field: string, value: any) => {
    switch (field) {
      case 'extendedAttributes':
        return additionalPropertiesToObject(value);
      case 'startDate':
        return DateTime.fromISO(value)
          .set({ hour: 0, minute: 0, second: 0 })
          .toISO({ includeOffset: false, suppressMilliseconds: true });
      case 'endDate':
        return DateTime.fromISO(value)
          .set({ hour: 23, minute: 59, second: 59 })
          .toISO({ includeOffset: false, suppressMilliseconds: true });
      default:
        return toBulkUpdateValue(field, value);
    }
  };

  const {
    status,
    startDate,
    endDate,
    extendedAttributes,
    displayRank,
    displayInfo,
  } = useMenuManagementBulkEditFields();

  const bulkEditFields = useMemo(() => {
    if (hasEditGroupPermission) {
      return {
        status,
        startDate,
        endDate,
        extendedAttributes,
        displayRank,
        ...displayInfo,
      };
    }
    return {};
  }, [
    displayInfo,
    displayRank,
    endDate,
    extendedAttributes,
    hasEditGroupPermission,
    startDate,
    status,
  ]);

  const bulkEditAdditionalMenus = useMemo(
    () =>
      hasDeleteGroupPermission
        ? [
            <MenuItem
              key="delete"
              onClick={() => bulkDeleteConfirmationDrawer.setOpen(true)}
            >
              <ListItemIcon>
                <DeleteOutlined fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>
                {t('GroupsDataGrid.actions.delete.label')}
              </ListItemText>
            </MenuItem>,
          ]
        : undefined,
    [bulkDeleteConfirmationDrawer, hasDeleteGroupPermission, t],
  );

  const handleBulkUpdate = async data => {
    const field = Object.keys(data)[0];
    const value = data[field];

    try {
      await triggerBulkEdit({
        ids: selectedRowIds,
        field: toBulkUpdateField(field),
        value: modifieldValue(field, value),
      }).unwrap();
      enqueueSnackbar(snackbarForBulkEdit(field), {
        severity: 'success',
      });
    } catch (_err) {}
  };

  const toolbar: ToolbarProps = {
    columns: { enabled: true },
    bulkEdit: {
      enabled: hasEditGroupPermission || hasDeleteGroupPermission,
      onValid: data => handleBulkUpdate(data),
      fields: bulkEditFields,
      additionalMenuItems: bulkEditAdditionalMenus,
    },
  };

  const initialState: GridInitialStatePremium = {
    pinnedColumns: { right: [t('SearchDataGrid.column.actions')] },
    columns: {
      columnVisibilityModel: {
        groupId: false,
        externalId: false,
        inheritParentOptions: false,
        minItemSelections: false,
        maxItemSelections: false,
        extendedAttributes: false,
        startDate: false,
        endDate: false,
        displayRank: false,
        ...displayInfoColumnVisibilityModel,
      },
    },
  };

  return { toolbar, initialState };
};
